import { DocumentType } from '../types/Types';
import { isRabotenNalog } from '../documents/docTypeChecks';

/** Presentation order shared by the top navigation and the Users permission catalogue. */
export const TOP_NAV_GROUPS = [
  { id: 'definitions', labelKey: 'sidebar.definitions' },
  { id: 'planning', labelKey: 'sidebar.planning' },
  { id: 'procurement', labelKey: 'sidebar.procurement' },
  { id: 'stockManagement', labelKey: 'sidebar.stockManagement' },
  { id: 'warehouse', labelKey: 'sidebar.warehouse' },
  { id: 'manufacturing', labelKey: 'sidebar.manufacturing' },
  { id: 'sales', labelKey: 'sidebar.sales' },
  { id: 'supportSectors', labelKey: 'sidebar.supportSectors' },
  { id: 'financial', labelKey: 'sidebar.financialManagement' },
] as const;

/** Sales section order and fixed screens are shared by the Navbar and RBAC editor. */
export const SALES_NAV_SECTIONS = [
  { id: 'documents', permissionKey: 'salesDocuments', labelKey: 'users.navDocuments' },
  { id: 'screens', permissionKey: 'salesScreens', labelKey: 'users.navScreens' },
] as const;

export const STATIC_SALES_SCREENS = [
  { code: '3Y10', labelKey: 'commercialContacts.title', fallback: 'Komercijalni kontakti' },
  { code: '3Y20', labelKey: 'sidebar.indirectBuyer', fallback: 'Indirekten kupuvac' },
  { code: '3Y30', labelKey: 'sidebar.settingSellingPricesNonMaterial', fallback: 'Promena prodazna cena' },
  { code: '3Y40', labelKey: 'sidebar.reservedContacts', fallback: 'Rezervirani kontakti' },
] as const;

export type NavigationGroupId = typeof TOP_NAV_GROUPS[number]['id'];
export type DocumentPlacement = NavigationGroupId | 'inventory' | 'qualityControl' | 'invoices';

/** These predicates describe menu placement only. Authorization remains in PermissionContext and server routes. */
export function documentBelongsTo(doc: DocumentType, placement: DocumentPlacement): boolean {
  switch (placement) {
    case 'planning': return doc.category === 'planning';
    case 'procurement': return (doc.category === 'material' && !doc.menuGroup) || doc.menuGroup === 'procurement' || (doc.category === 'non-material' && doc.subCategory === 'procurement');
    case 'stockManagement': return doc.menuGroup === 'warehouse';
    case 'warehouse': return doc.menuGroup === 'storehouse';
    case 'manufacturing': return isRabotenNalog(doc) || doc.menuGroup === 'manufacturing' || doc.menuGroup === 'production';
    case 'sales': return (doc.category === 'non-material' && doc.subCategory === 'sales') || doc.menuGroup === 'sales';
    case 'inventory': return doc.menuGroup === 'inventory';
    case 'qualityControl': return doc.menuGroup === 'quality-control';
    case 'financial': return doc.menuGroup === 'finances';
    case 'invoices': return doc.subCategory === 'invoice';
    default: return false;
  }
}

export function sortSalesDocuments(docs: DocumentType[]): DocumentType[] {
  return [...docs].sort((a, b) => {
    const numA = parseInt(a.code, 10);
    const numB = parseInt(b.code, 10);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
    return a.code.localeCompare(b.code);
  });
}

export function sortManufacturingDocuments(docs: DocumentType[]): DocumentType[] {
  return [...docs].sort((a, b) => {
    const numA = Number(a.code);
    const numB = Number(b.code);
    const aNum = !Number.isNaN(numA);
    const bNum = !Number.isNaN(numB);
    if (!aNum && !bNum) return a.code.localeCompare(b.code);
    if (!aNum) return -1;
    if (!bNum) return 1;
    return numA - numB;
  });
}

export function documentsForPlacement(docs: DocumentType[], placement: DocumentPlacement): DocumentType[] {
  return docs.filter((doc) => documentBelongsTo(doc, placement));
}

/** A document appears in each section where the Navbar links to it. */
export function groupDocumentsForPermissions(docs: DocumentType[]): Record<DocumentPlacement | 'additional', DocumentType[]> {
  const groups = {} as Record<DocumentPlacement | 'additional', DocumentType[]>;
  const order: DocumentPlacement[] = ['planning', 'procurement', 'stockManagement', 'warehouse', 'manufacturing', 'sales', 'inventory', 'qualityControl', 'financial'];
  const shownCodes = new Set<string>();
  for (const placement of order) {
    const placed = documentsForPlacement(docs, placement);
    groups[placement] = placement === 'manufacturing'
      ? sortManufacturingDocuments(placed)
      : ['procurement', 'stockManagement', 'warehouse', 'sales', 'inventory', 'qualityControl'].includes(placement)
        ? sortSalesDocuments(placed)
        : placed;
    groups[placement].forEach((doc) => shownCodes.add(doc.code));
  }
  groups.invoices = documentsForPlacement(docs, 'invoices');
  groups.additional = docs.filter((doc) => !shownCodes.has(doc.code));
  return groups;
}
